import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CASH_ACCOUNT_KIND_LABELS,
  CURRENCY_DEFS,
  type CashAccountKind,
} from '@goyone/shared';
import { api, qs } from '../../lib/api';
import { date, money, plural, WORDS } from '../../lib/format';
import { useAuth } from '../../auth/AuthProvider';
import { Icon } from '../../components/Icon';
import { ExchangeRatesModal } from '../../components/ExchangeRatesModal';
import { baseHint, fxMoney, isForeignCurrency, rateText } from '../../lib/currency';
import { Badge, EmptyState, ErrorState, Modal, PageHead, Stat } from '../../components/ui';
import { SearchBox } from '../../components/ui';
import { useListState } from '../../lib/hooks';
import { CodeText } from '../../components/Code';
import { ExportMenu } from '../../components/ExportMenu';
import {
  describeFilters,
  FilterButton,
  FilterChips,
  FilterScope,
  HeaderFilter,
  matchesSearch,
  useLocalFilterTable,
  useMemoryListState,
  type FilterDef,
  type LocalAccessors,
} from '../../components/ColumnFilter';
import { exportDay, exportNotes, exportPeriod, type TableExportDoc } from '../../lib/tableExport';

interface CashAccount {
  id: string;
  name: string;
  kind: CashAccountKind;
  bankName: string | null;
  accountNumber: string | null;
  iban: string | null;
  currency: string;
  isActive: boolean;
  isDefault: boolean;
  ledgerAccountId: string;
  ledgerCode: string;
  /** بعملة الخزينة. */
  balance: number;
  openingBalance: number;
  movement: number;
  /** سعر اليوم، والرصيد بالجنيه به. */
  rate: number | null;
  baseBalance: number | null;
}

interface LedgerOption {
  id: string;
  code: string;
  name: string;
  currency?: string;
  type: string;
}

interface StatementRow {
  id: string;
  date: string;
  number: string;
  source: string;
  memo: string | null;
  in: number;
  out: number;
  balance: number;
  /** للخزينة الأجنبية: سعر الحركة ومقابلها بالجنيه. */
  rate?: number | null;
  base?: number;
}

/** أعمدة فلتر الخزائن — محلّية: القائمة تصل كلها في طلب واحد. */
const CASH_FILTERS: FilterDef[] = [
  { key: 'name', label: 'الخزينة', kind: 'text', sortable: true },
  {
    key: 'kind',
    label: 'النوع',
    kind: 'enum',
    options: (Object.keys(CASH_ACCOUNT_KIND_LABELS) as CashAccountKind[]).map((k) => ({
      value: k,
      label: CASH_ACCOUNT_KIND_LABELS[k],
    })),
  },
  { key: 'ledgerCode', label: 'الحساب في الدليل', kind: 'text', sortable: true },
  { key: 'accountNumber', label: 'رقم الحساب', kind: 'text' },
  { key: 'openingBalance', label: 'افتتاحي', kind: 'number', sortable: true },
  { key: 'movement', label: 'صافي الحركة', kind: 'number', sortable: true },
  { key: 'balance', label: 'الرصيد', kind: 'number', sortable: true },
];

const CASH_ACCESSORS: LocalAccessors<CashAccount> = {
  accountNumber: (a) => a.accountNumber ?? a.iban,
};

/** أعمدة فلتر كشف الحركة — في النافذة، فحالتها في الذاكرة لا في الرابط. */
const STATEMENT_FILTERS: FilterDef[] = [
  { key: 'date', label: 'التاريخ', kind: 'date', sortable: true },
  { key: 'number', label: 'القيد', kind: 'text', sortable: true },
  { key: 'memo', label: 'البيان', kind: 'text', noList: true },
  { key: 'in', label: 'وارد', kind: 'number', sortable: true },
  { key: 'out', label: 'صادر', kind: 'number', sortable: true },
];

/* الخلايا الفارغة «—» في الكشف أصفارٌ في البيانات؛ والمدى يُقرأ على ما يُرى. */
const STATEMENT_ACCESSORS: LocalAccessors<StatementRow> = {
  in: (r) => (r.in ? r.in : null),
  out: (r) => (r.out ? r.out : null),
};

/**
 * الخزائن والحسابات البنكية.
 *
 * الرصيد المعروض محسوب من القيود المرحَّلة لا من رقم مخزَّن، فما تراه هنا
 * هو ما يقوله الدفتر. اختلافه عن كشف البنك يعني حركة لم تُسجَّل بعد، وهو
 * بالضبط ما تُفتح الشاشة لاكتشافه.
 */
export function CashAccountsList() {
  const { atLeast } = useAuth();
  const queryClient = useQueryClient();
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<CashAccount | 'new' | null>(null);
  const [viewing, setViewing] = useState<CashAccount | null>(null);
  const [showRates, setShowRates] = useState(false);

  const canEdit = atLeast('MANAGER');

  const list = useQuery({
    queryKey: ['cash-accounts', showInactive],
    queryFn: () =>
      api<{ items: CashAccount[]; total: number }>(
        `/finance/cash-accounts${qs({ includeInactive: showInactive || undefined })}`,
      ),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['cash-accounts'] });
    setEditing(null);
  };

  const banks = (list.data?.items ?? []).filter((a) => a.kind === 'BANK');
  const cash = (list.data?.items ?? []).filter((a) => a.kind !== 'BANK');

  /* البحث وفلاتر الأعمدة والفرز — في المتصفح، والحالة في الرابط. */
  const { state: listState, patch } = useListState();
  const [search, setSearch] = useState(listState.q ?? '');
  const table = useLocalFilterTable({
    rows: list.data?.items,
    defs: CASH_FILTERS,
    accessors: CASH_ACCESSORS,
    state: listState,
    patch,
    search: search
      ? (a: CashAccount) =>
          matchesSearch(search, a.name, a.bankName, a.accountNumber, a.iban, a.ledgerCode)
      : undefined,
  });
  const shown = table.rows;

  const buildExport = (): TableExportDoc => ({
    title: 'البنوك والخزائن',
    sheets: [
      {
        title: 'البنوك والخزائن',
        period: { label: `كما في ${exportDay(new Date().toISOString())}` },
        sections: [
          {
            columns: [
              { key: 'name', label: 'الخزينة' },
              { key: 'kind', label: 'النوع' },
              { key: 'bankName', label: 'البنك' },
              { key: 'ledgerCode', label: 'الحساب في الدليل' },
              { key: 'accountNumber', label: 'رقم الحساب', kind: 'code' },
              { key: 'openingBalance', label: 'افتتاحي', kind: 'money', total: true },
              { key: 'movement', label: 'صافي الحركة', kind: 'money', total: true },
              { key: 'balance', label: 'الرصيد', kind: 'money', total: true },
              { key: 'state', label: 'الحالة', wrap: false },
            ],
            rows: shown.map((a) => ({
              name: a.name,
              kind: CASH_ACCOUNT_KIND_LABELS[a.kind],
              bankName: a.bankName,
              ledgerCode: a.ledgerCode,
              accountNumber: a.accountNumber ?? a.iban,
              openingBalance: a.openingBalance,
              movement: a.movement,
              balance: a.balance,
              state: a.isActive ? 'نشطة' : 'موقوفة',
            })),
          },
        ],
        notes: exportNotes({
          q: search,
          conditions: describeFilters(CASH_FILTERS, table.filters),
          count: shown.length,
          total: shown.length,
          truncated: false,
          unit: 'الخزائن',
        }),
      },
    ],
  });

  return (
    <div className="page">
      <PageHead
        title="البنوك والخزائن"
        subtitle="أرصدة محسوبة من القيود المرحَّلة"
        actions={
          <>
            <button className="btn" onClick={() => setShowRates(true)}>
              أسعار الصرف
            </button>
            {canEdit && (
              <button className="btn btn-primary" onClick={() => setEditing('new')}>
                <Icon name="plus" size={15} />
                خزينة جديدة
              </button>
            )}
          </>
        }
      />

      <div className="stats">
        <Stat
          label="إجمالي النقدية"
          value={<span className="num">{list.data ? money(list.data.total) : '—'}</span>}
          note="بالجنيه — الخزائن الأجنبية بسعر اليوم"
        />
        <Stat
          label="في البنوك"
          value={<span className="num">{money(banks.reduce((s, a) => s + (a.baseBalance ?? 0), 0))}</span>}
          note={plural(banks.length, WORDS.account)}
        />
        <Stat
          label="نقدًا ومحافظ"
          value={<span className="num">{money(cash.reduce((s, a) => s + (a.baseBalance ?? 0), 0))}</span>}
          note={plural(cash.length, WORDS.till)}
        />
      </div>

      <div className="toolbar sticky-bar">
        <SearchBox
          value={search}
          onChange={(v) => {
            setSearch(v);
            patch({ q: v || undefined });
          }}
          placeholder="بحث بالاسم أو البنك أو رقم الحساب"
        />
        <label className="row" style={{ cursor: 'pointer', gap: 6 }}>
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          <span className="small">إظهار الموقوفة</span>
        </label>
        <ExportMenu build={buildExport} disabled={!shown.length} />
        <span className="row" style={{ marginInlineStart: 'auto', gap: 'var(--sp-2)' }}>
          <FilterButton {...table.button} />
        </span>
      </div>

      <FilterChips {...table.chips} />

      {list.isError && <ErrorState error={list.error} onRetry={() => void list.refetch()} />}

      <FilterScope {...table.scope}>
      <div className="card">
        <div className="table-wrap sticky-head">
          <table className="table">
            <thead>
              <tr>
                <th>
                  الخزينة
                  <HeaderFilter colKey="name" />
                </th>
                <th>
                  النوع
                  <HeaderFilter colKey="kind" />
                </th>
                <th>
                  الحساب في الدليل
                  <HeaderFilter colKey="ledgerCode" />
                </th>
                <th>
                  رقم الحساب
                  <HeaderFilter colKey="accountNumber" />
                </th>
                <th>
                  افتتاحي
                  <HeaderFilter colKey="openingBalance" />
                </th>
                <th>
                  صافي الحركة
                  <HeaderFilter colKey="movement" />
                </th>
                <th>
                  الرصيد
                  <HeaderFilter colKey="balance" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {shown.map((a) => (
                <tr key={a.id}>
                  <td className="cell-strong">
                    {a.name}
                    {a.isDefault && (
                      <span className="tiny mute"> — الافتراضية</span>
                    )}
                    {!a.isActive && (
                      <Badge kind="neutral">موقوفة</Badge>
                    )}
                    {a.bankName && <div className="tiny mute">{a.bankName}</div>}
                  </td>
                  <td className="cell-mute">{CASH_ACCOUNT_KIND_LABELS[a.kind]}</td>
                  <td className="num cell-mute">{a.ledgerCode}</td>
                  <td className="num cell-mute" dir="ltr">
                    {a.accountNumber ?? a.iban ?? '—'}
                  </td>
                  <td className="num cell-mute">{fxMoney(a.openingBalance, a.currency)}</td>
                  <td className={`num ${a.movement < 0 ? 'neg' : ''}`}>
                    {fxMoney(a.movement, a.currency)}
                  </td>
                  <td className="num cell-strong">
                    {fxMoney(a.balance, a.currency)}
                    {isForeignCurrency(a.currency) && (
                      <div className="tiny mute">{baseHint(a.balance, a.rate)}</div>
                    )}
                  </td>
                  <td className="end">
                    <div className="row" style={{ gap: 4, justifyContent: 'flex-end' }}>
                      <button className="btn btn-sm btn-ghost" onClick={() => setViewing(a)}>
                        كشف الحركة
                      </button>
                      {canEdit && (
                        <button
                          className="btn btn-sm btn-icon btn-ghost"
                          onClick={() => setEditing(a)}
                          aria-label={`تعديل ${a.name}`}
                        >
                          <Icon name="edit" size={15} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {list.data?.items.length === 0 && (
          <EmptyState
            title="لا توجد خزائن"
            text="أنشئ حسابًا بنكيًا أو خزينة نقدية ليصبح ممكنًا تسجيل التحصيل والسداد والمصروفات."
            action={
              canEdit && (
                <button className="btn btn-primary" onClick={() => setEditing('new')}>
                  خزينة جديدة
                </button>
              )
            }
          />
        )}

        {!!list.data?.items.length && shown.length === 0 && (
          <EmptyState title="لا نتائج" text="لا خزينة تطابق هذا البحث أو الفلتر." />
        )}
      </div>
      </FilterScope>

      {editing && (
        <CashAccountModal
          account={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={refresh}
        />
      )}

      {viewing && <StatementModal account={viewing} onClose={() => setViewing(null)} />}

      {showRates && (
        <ExchangeRatesModal canEdit={canEdit} onClose={() => setShowRates(false)} />
      )}
    </div>
  );
}

/* ========================================================================== */

function CashAccountModal({
  account,
  onClose,
  onSaved,
}: {
  account: CashAccount | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    name: account?.name ?? '',
    kind: account?.kind ?? ('BANK' as CashAccountKind),
    ledgerAccountId: account?.ledgerAccountId ?? '',
    bankName: account?.bankName ?? '',
    accountNumber: account?.accountNumber ?? '',
    iban: account?.iban ?? '',
    currency: account?.currency ?? 'EGP',
    openingBalance: String(account?.openingBalance ?? 0),
    isDefault: account?.isDefault ?? false,
    isActive: account?.isActive ?? true,
  });

  const set = (key: string, value: unknown) => setForm((f) => ({ ...f, [key]: value }));

  /*
   * الحسابات المعروضة أصول قابلة للترحيل فقط.
   *
   * ربط خزينة بحساب إيراد أو بحساب تجميعي ينتج قيودًا لا معنى لها، والتصفية
   * هنا تمنع الخطأ قبل وقوعه بدل أن ترفضه رسالة بعد الحفظ.
   */
  const accounts = useQuery({
    queryKey: ['ledger', 'assets'],
    queryFn: () => api<LedgerOption[]>('/accounting/accounts/postable?type=ASSET'),
  });

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: form.name,
        kind: form.kind,
        ledgerAccountId: form.ledgerAccountId,
        bankName: form.bankName || undefined,
        accountNumber: form.accountNumber || undefined,
        iban: form.iban || undefined,
        currency: form.currency,
        openingBalance: Number(form.openingBalance) || 0,
        isDefault: form.isDefault,
        ...(account ? { isActive: form.isActive } : {}),
      };
      return account
        ? api(`/finance/cash-accounts/${account.id}`, { method: 'PATCH', body })
        : api('/finance/cash-accounts', { method: 'POST', body });
    },
    onSuccess: onSaved,
  });

  const remove = useMutation({
    mutationFn: () =>
      api(`/finance/cash-accounts/${account!.id}`, { method: 'DELETE' }),
    onSuccess: onSaved,
  });

  return (
    <Modal
      title={account ? `تعديل ${account.name}` : 'خزينة جديدة'}
      onClose={onClose}
      footer={
        <>
          {account && (
            <button
              className="btn btn-danger me-auto"
              type="button"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
            >
              حذف
            </button>
          )}
          <button className="btn" onClick={onClose} type="button">
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={form.name.trim().length < 2 || !form.ledgerAccountId || save.isPending}
          >
            {save.isPending ? 'جارٍ الحفظ' : 'حفظ'}
          </button>
        </>
      }
    >
      {save.isError && (
        <div className="alert alert-danger mb-4">
          {save.error instanceof Error ? save.error.message : 'تعذّر الحفظ.'}
        </div>
      )}

      <div className="form-grid">
        <div className="field span-2">
          <label className="label req" htmlFor="ca-name">
            الاسم
          </label>
          <input
            id="ca-name"
            className="input"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            autoFocus
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="ca-kind">
            النوع
          </label>
          <select
            id="ca-kind"
            className="select"
            value={form.kind}
            onChange={(e) => set('kind', e.target.value)}
          >
            {(Object.keys(CASH_ACCOUNT_KIND_LABELS) as CashAccountKind[]).map((k) => (
              <option key={k} value={k}>
                {CASH_ACCOUNT_KIND_LABELS[k]}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label className="label req" htmlFor="ca-ledger">
            الحساب المقابل في الدليل
          </label>
          <select
            id="ca-ledger"
            className="select"
            value={form.ledgerAccountId}
            onChange={(e) => {
              set('ledgerAccountId', e.target.value);
              // عملة الخزينة تتبع حسابها في الدليل إن كان له عملة أجنبية.
              const picked = accounts.data?.find((a) => a.id === e.target.value);
              if (picked?.currency && picked.currency !== 'EGP') set('currency', picked.currency);
            }}
          >
            <option value="">اختر حسابًا</option>
            {accounts.data?.map((a) => (
              <option key={a.id} value={a.id}>
                {a.code} — {a.name}
              </option>
            ))}
          </select>
          <span className="tiny mute">تُرحَّل إليه كل حركات هذه الخزينة.</span>
        </div>

        <div className="field">
          <label className="label" htmlFor="ca-currency">
            العملة
          </label>
          <select
            id="ca-currency"
            className="select"
            value={form.currency}
            onChange={(e) => set('currency', e.target.value)}
          >
            {CURRENCY_DEFS.map((c) => (
              <option key={c.code} value={c.code}>
                {c.label} ({c.symbol})
              </option>
            ))}
          </select>
          <span className="tiny mute">
            الحركات والرصيد بهذه العملة، والدفاتر بالجنيه بسعر يوم كل حركة. تُقفل العملة بعد أول
            حركة على حسابها.
          </span>
        </div>

        {form.kind === 'BANK' && (
          <>
            <div className="field">
              <label className="label" htmlFor="ca-bank">
                اسم البنك والفرع
              </label>
              <input
                id="ca-bank"
                className="input"
                value={form.bankName}
                onChange={(e) => set('bankName', e.target.value)}
              />
            </div>

            <div className="field">
              <label className="label" htmlFor="ca-number">
                رقم الحساب
              </label>
              <input
                id="ca-number"
                className="input num"
                value={form.accountNumber}
                onChange={(e) => set('accountNumber', e.target.value)}
                dir="ltr"
              />
            </div>

            <div className="field span-2">
              <label className="label" htmlFor="ca-iban">
                الآيبان
              </label>
              <input
                id="ca-iban"
                className="input num"
                value={form.iban}
                onChange={(e) => set('iban', e.target.value)}
                dir="ltr"
              />
            </div>
          </>
        )}

        <div className="field">
          <label className="label" htmlFor="ca-opening">
            الرصيد الافتتاحي{isForeignCurrency(form.currency) ? ` (${form.currency})` : ''}
          </label>
          <input
            id="ca-opening"
            className="input num"
            type="number"
            step="any"
            value={form.openingBalance}
            onChange={(e) => set('openingBalance', e.target.value)}
          />
          <span className="tiny mute">الرصيد قبل أول قيد في النظام.</span>
        </div>
      </div>

      <div className="col mt-4">
        <label className="row" style={{ cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={form.isDefault}
            onChange={(e) => set('isDefault', e.target.checked)}
          />
          <span className="small">الافتراضية عند تسجيل تحصيل أو سداد</span>
        </label>

        {account && (
          <label className="row" style={{ cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => set('isActive', e.target.checked)}
            />
            <span className="small">نشطة</span>
          </label>
        )}
      </div>
    </Modal>
  );
}

/* ========================================================================== */

function StatementModal({ account, onClose }: { account: CashAccount; onClose: () => void }) {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const statement = useQuery({
    queryKey: ['cash-statement', account.id, from, to],
    queryFn: () =>
      api<{
        currency: string;
        rate: number | null;
        closingBase: number | null;
        opening: number;
        closing: number;
        totalIn: number;
        totalOut: number;
        rows: StatementRow[];
      }>(`/finance/cash-accounts/${account.id}/statement${qs({ from: from || undefined, to: to || undefined })}`),
  });

  /*
   * بحثٌ وفلاتر أعمدة داخل الكشف — في الذاكرة لا في الرابط: النافذة تُغلق
   * وتُفتح لخزينة أخرى، وشروطها لا تخصّ صفحة الخزائن. والرصيد الجاري يبقى
   * كما حسبه الخادم للصف، فإخفاء صفوفٍ لا يغيّره.
   */
  const memory = useMemoryListState();
  const [stSearch, setStSearch] = useState('');
  const stTable = useLocalFilterTable({
    rows: statement.data?.rows,
    defs: STATEMENT_FILTERS,
    accessors: STATEMENT_ACCESSORS,
    state: memory.state,
    patch: memory.patch,
    param: 'st',
    search: stSearch ? (r: StatementRow) => matchesSearch(stSearch, r.number, r.memo) : undefined,
  });

  const buildStatementExport = (): TableExportDoc => ({
    title: `كشف حركة ${account.name}`,
    sheets: [
      {
        title: `كشف حركة ${account.name}`,
        period: exportPeriod(from || undefined, to || undefined),
        sections: [
          {
            columns: [
              { key: 'date', label: 'التاريخ', kind: 'date' },
              { key: 'number', label: 'القيد', kind: 'code' },
              { key: 'memo', label: 'البيان', minWidth: 24 },
              { key: 'in', label: 'وارد', kind: 'money', total: true },
              { key: 'out', label: 'صادر', kind: 'money', total: true },
              { key: 'balance', label: 'الرصيد', kind: 'money' },
            ],
            rows: stTable.rows.map((r) => ({
              date: exportDay(r.date),
              number: r.number,
              memo: r.memo,
              in: r.in || null,
              out: r.out || null,
              balance: r.balance,
            })),
          },
        ],
        notes: exportNotes({
          q: stSearch,
          conditions: describeFilters(STATEMENT_FILTERS, stTable.filters),
          count: stTable.rows.length,
          total: stTable.rows.length,
          truncated: false,
          unit: 'الحركات',
        }),
      },
    ],
  });
  // الكشف بعملة الخزينة، والمقابل بالجنيه بجواره للخزينة الأجنبية.
  const cur = statement.data?.currency ?? account.currency;
  const foreign = isForeignCurrency(cur);
  const m = (v: unknown) => fxMoney(v, cur);

  return (
    <Modal
      title={`كشف حركة ${account.name}`}
      onClose={onClose}
      wide
      footer={
        <button className="btn btn-primary" onClick={onClose}>
          إغلاق
        </button>
      }
    >
      <div className="form-grid mb-4">
        <div className="field">
          <label className="label" htmlFor="st-from">
            من تاريخ
          </label>
          <input
            id="st-from"
            className="input"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </div>
        <div className="field">
          <label className="label" htmlFor="st-to">
            إلى تاريخ
          </label>
          <input
            id="st-to"
            className="input"
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </div>
      </div>

      {statement.isPending && <div className="skeleton" style={{ height: 200 }} />}

      {statement.data && (
        <>
          <div className="stats">
            <Stat
              label="رصيد أول المدة"
              value={<span className="num">{m(statement.data.opening)}</span>}
            />
            <Stat
              label="الوارد"
              value={<span className="num pos">{m(statement.data.totalIn)}</span>}
            />
            <Stat
              label="الصادر"
              value={<span className="num neg">{m(statement.data.totalOut)}</span>}
            />
            <Stat
              label="رصيد آخر المدة"
              value={<span className="num">{m(statement.data.closing)}</span>}
              note={foreign ? baseHint(statement.data.closing, statement.data.rate) : undefined}
            />
          </div>

          <div className="toolbar mt-4">
            <SearchBox value={stSearch} onChange={setStSearch} placeholder="بحث برقم القيد أو البيان" />
            <ExportMenu build={buildStatementExport} disabled={!stTable.rows.length} />
            <span className="row" style={{ marginInlineStart: 'auto', gap: 'var(--sp-2)' }}>
              <FilterButton {...stTable.button} />
            </span>
          </div>
          <FilterChips {...stTable.chips} />

          {/* الرأس يلتصق بأعلى الصندوق (`.table th` لاصقٌ أصلًا والصندوق سياق التمرير). */}
          <FilterScope {...stTable.scope}>
          <div className="table-wrap scroll-box" style={{ maxHeight: 340 }}>
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 110 }}>
                    التاريخ
                    <HeaderFilter colKey="date" />
                  </th>
                  <th style={{ width: 110 }}>
                    القيد
                    <HeaderFilter colKey="number" />
                  </th>
                  <th>
                    البيان
                    <HeaderFilter colKey="memo" />
                  </th>
                  <th style={{ width: 110 }}>
                    وارد
                    <HeaderFilter colKey="in" />
                  </th>
                  <th style={{ width: 110 }}>
                    صادر
                    <HeaderFilter colKey="out" />
                  </th>
                  <th style={{ width: 120 }}>الرصيد</th>
                  {foreign && <th style={{ width: 90 }}>السعر</th>}
                  {foreign && <th style={{ width: 120 }}>بالجنيه</th>}
                </tr>
              </thead>
              <tbody>
                {stTable.rows.map((r) => (
                  <tr key={r.id}>
                    <td className="num cell-mute">{date(r.date)}</td>
                    <td className="num cell-mute">{r.number}</td>
                    <td>
                      <CodeText text={r.memo ?? '—'} />
                    </td>
                    <td className="num">{r.in ? m(r.in) : '—'}</td>
                    <td className="num">{r.out ? m(r.out) : '—'}</td>
                    <td className={`num cell-strong${r.balance < 0 ? ' neg' : ''}`}>{m(r.balance)}</td>
                    {foreign && <td className="num cell-mute">{rateText(r.rate)}</td>}
                    {foreign && <td className="num cell-mute">{money(r.base ?? 0)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </FilterScope>

          {statement.data.rows.length === 0 && (
            <EmptyState title="لا حركة في هذه الفترة" text="غيّر النطاق الزمني أو اتركه فارغًا لعرض كل الحركة." />
          )}
          {statement.data.rows.length > 0 && stTable.rows.length === 0 && (
            <EmptyState title="لا نتائج" text="لا حركة تطابق هذا البحث أو الفلتر." />
          )}
        </>
      )}
    </Modal>
  );
}
